import {
  AfterViewInit,
  Component,
  forwardRef,
  inject,
  Input,
  OnDestroy,
  OnInit,
  viewChild,
  type FactoryProvider,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, ReactiveFormsModule, UntypedFormControl } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { FormFieldControl, useFormFieldControlForwarder } from '@sb/ui/form-field';
import { SelectComponent, SelectItem } from '@sb/ui/select';
import pickBy from 'lodash-es/pickBy';
import { Subscription } from 'rxjs';

import { fieldChangeWithStart } from '../../../../forms/form.helper';
import {
  IntegrationEmployeeField,
  IntegrationEmployeeSync,
} from '../../../../reducers/orm/integration/integration-configure.model';
import { getAppSyncValues } from '../app-configure.helper';

export const INPUT_CONTROL_VALUE_ACCESSOR: any = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => EmployeeSyncComponent),
  multi: true,
};

// The inner `<sb-select>` lives in this component's view, so the form field's
// contentChild(FormFieldControl) can't reach it. Publish a forwarder at the host
// that relays the field's label/description ids and invalid state down to it.
const FORM_FIELD_CONTROL: FactoryProvider = {
  provide: FormFieldControl,
  useFactory: (self: EmployeeSyncComponent) => self.formFieldControl,
  deps: [forwardRef(() => EmployeeSyncComponent)],
};

@Component({
  selector: 'employee-sync',
  template: ` <sb-select
    [items]="items"
    [formControl]="selectControl"
    [displayTree]="true"
    [searchable]="true"
    [multiSelect]="true"
    [showSelectAll]="true"
  ></sb-select>`,
  providers: [INPUT_CONTROL_VALUE_ACCESSOR, FORM_FIELD_CONTROL],
  imports: [SelectComponent, ReactiveFormsModule],
})
export class EmployeeSyncComponent implements OnInit, AfterViewInit, OnDestroy, ControlValueAccessor {
  private translate = inject(TranslateService);

  innerControl = viewChild(FormFieldControl);
  formFieldControl = useFormFieldControlForwarder(this.innerControl);

  @Input()
  fields: IntegrationEmployeeField[] = Object.values(IntegrationEmployeeField);

  selectControl = new UntypedFormControl([]);
  onTouched: () => void;
  onModelChange: (val: Record<string, boolean>) => void;
  items: SelectItem[] = [];
  private value: Record<string, boolean>;
  private dataSubs = new Subscription();

  ngOnInit() {
    this.items = this.fields.map((field) => ({
      text: this.translate.instant(IntegrationEmployeeSync[field]),
      value: field,
    }));
  }

  ngAfterViewInit() {
    this.dataSubs.add(
      fieldChangeWithStart(this.selectControl).subscribe({
        next: (val: string[]) => {
          if (!val) {
            return;
          }
          this.updateVal(val);
          this.onModelChange(this.value);
        },
      }),
    );
  }

  ngOnDestroy() {
    this.dataSubs.unsubscribe();
  }

  // called by the reactive form control
  registerOnChange(fn: (val: Record<string, boolean>) => void) {
    // assigns to our internal model change method
    this.onModelChange = fn;
  }

  // called by the reactive form control
  registerOnTouched(fn: () => any): void {
    this.onTouched = fn;
  }

  writeValue(value: Record<string, boolean>) {
    if (!value) {
      return;
    }
    const keys = Object.keys(pickBy(value));
    this.updateVal(keys);
    this.selectControl.setValue(keys);
  }

  updateVal(values: string[]) {
    this.value = getAppSyncValues(this.fields, values);
  }

  setDisabledState(isDisabled: boolean): void {
    if (isDisabled) {
      this.selectControl.disable();
    } else {
      this.selectControl.enable();
    }
  }
}
